/**
 * Симуляция «Инерция» — опыт Галилея из описания коллеги (TEOR-11). Шар скатывается с горки
 * на стол; за горкой стол покрыт песком, щебнем, асфальтом, тканью или льдом. Чем слабее
 * стол действует на шар, тем дальше шар катится; на идеально гладком столе (мысленный опыт)
 * шар не останавливается — сохраняет скорость.
 *
 * Шар останавливает сопротивление качению (materialy.ts: «качение»), а не трение скольжения:
 * катящийся шар не скользит. Путь по покрытию s ≈ h / c (энергия m·g·h уходит на работу
 * силы c·m·g) — сверено тестом tests/pesochnica.test.ts.
 */
import { МАТЕРИАЛЫ } from '../pesochnica/materialy.ts'
import { G_ЗЕМЛЯ, type Сцена } from '../pesochnica/scena.ts'
import type { Итог, Симуляция, Значения } from './opisanie.ts'

const УГОЛ = Math.PI / 6
/** Высота верха горки, м: шар ставится на склон не выше. */
const ВЕРХ = 0.32
/** Покрытие начинается сразу за скруглением подножия горки. */
export const НАЧАЛО_ПОКРЫТИЯ = 0.1
/** Длина стола за горкой, м. */
export const ДЛИНА_СТОЛА = 6

const ПОКРЫТИЯ = [
  { значение: 'песок', подпись: 'песок' },
  { значение: 'щебень', подпись: 'щебень' },
  { значение: 'асфальт', подпись: 'асфальт' },
  { значение: 'ткань', подпись: 'ткань' },
  { значение: 'лёд', подпись: 'лёд' },
  { значение: 'гладь', подпись: 'идеально гладкая поверхность' },
] as const

const ШАРЫ = [
  { значение: 'сталь', подпись: 'стальной' },
  { значение: 'стекло', подпись: 'стеклянный' },
  { значение: 'резина', подпись: 'резиновый' },
  { значение: 'дерево', подпись: 'деревянный' },
] as const

function число(з: Значения, ключ: string, запас: number): number {
  const v = з[ключ]
  return typeof v === 'number' ? v : запас
}

function строка(з: Значения, ключ: string, запас: string): string {
  const v = з[ключ]
  return typeof v === 'string' ? v : запас
}

/** Горка, стол с покрытием и шар в руке на высоте h над столом (по точке касания). */
export function сценаГорки(з: Значения): Сцена {
  const h = число(з, 'высота', 0.1)
  const r = число(з, 'радиус', 0.02)
  const покрытие = строка(з, 'покрытие', 'песок')
  const мат = строка(з, 'шар', 'сталь')
  const xВерх = -ВЕРХ / Math.tan(УГОЛ)
  const s = h / Math.sin(УГОЛ)
  const масса = МАТЕРИАЛЫ[мат].плотность * (4 / 3) * Math.PI * r ** 3
  return {
    версия: 1,
    название: 'Шар с горки',
    мир: { g: G_ЗЕМЛЯ },
    поверхности: [
      {
        id: 'стол',
        точки: [[xВерх - 0.15, ВЕРХ], [xВерх, ВЕРХ], [0, 0], [ДЛИНА_СТОЛА, 0], [ДЛИНА_СТОЛА, -0.05]],
        материал: 'дерево',
        покрытия: [{ от: НАЧАЛО_ПОКРЫТИЯ, до: ДЛИНА_СТОЛА, материал: покрытие }],
        скругление: 0.06,
        толщина: 0.05,
      },
      { id: 'пол', точки: [[xВерх - 1, -0.8], [ДЛИНА_СТОЛА + 3, -0.8]], материал: 'дерево', толщина: 0.05 },
    ],
    тела: [
      {
        id: 'шар',
        форма: 'шар',
        радиус: r,
        x: -s * Math.cos(УГОЛ) + r * Math.sin(УГОЛ),
        y: s * Math.sin(УГОЛ) + r * Math.cos(УГОЛ),
        масса,
        материал: мат,
        держать: true,
        подпись: 'шар',
      },
    ],
    связи: [],
  }
}

/** Путь шара по покрытию: пока катится — текущий, после остановки — итог. */
const путьПоПокрытию: Итог = {
  подпись: 'путь по покрытию',
  значение: (_м, с) => {
    const шар = с.тела.find((т) => т.id === 'шар')
    if (!шар || шар.держат) return null
    if (шар.x > ДЛИНА_СТОЛА) return 'уехал за край стола — не остановился'
    if (шар.x < НАЧАЛО_ПОКРЫТИЯ) return шар.покоится ? '0 м' : null
    const d = (шар.x - НАЧАЛО_ПОКРЫТИЯ).toFixed(2).replace('.', ',')
    return шар.покоится && с.t > 0.3 ? `${d} м — остановился` : `${d} м…`
  },
}

const скоростьШара: Итог = {
  подпись: 'скорость шара',
  значение: (_м, с) => {
    const шар = с.тела.find((т) => т.id === 'шар')
    if (!шар) return null
    return `${Math.hypot(шар.v[0], шар.v[1]).toFixed(2).replace('.', ',')} м/с`
  },
}

const ВИД = { высота: 0.4, низ: -0.07, x0: -ВЕРХ / Math.tan(УГОЛ) - 0.12, следить: 'шар' }
const РАЗМЕТКА = { y: 0, от: НАЧАЛО_ПОКРЫТИЯ, до: ДЛИНА_СТОЛА, ноль: НАЧАЛО_ПОКРЫТИЯ, шаг: 0.05, подпись: 0.25, толщина: 0.05 }
const ВЫСОТА = { вид: 'ползунок', ключ: 'высота', подпись: 'Высота горки', min: 0.05, max: 0.3, шаг: 0.01, единица: 'см', множитель: 100, знаков: 0 } as const
const ПОКРЫТИЕ = { вид: 'выбор', ключ: 'покрытие', подпись: 'Покрытие стола', варианты: ПОКРЫТИЯ } as const

export const ИНЕРЦИЯ: Симуляция = {
  id: 'inerciya',
  название: 'Инерция',
  раздел: 'Механика',
  тема: 'Инерция',
  класс: 7,
  кратко: 'шар с горки катится по песку, ткани, льду — что его останавливает',
  экраны: [
    {
      id: 'gorka',
      название: 'Почему шар останавливается',
      задание: 'Отпусти шар с горки и посмотри, что с ним станет на столе.',
      начальные: { высота: 0.1, покрытие: 'ткань' },
      сцена: сценаГорки,
      управление: [ВЫСОТА],
      показать: { силы: false, значения: false, скорость: true, стробоскоп: false },
      приборы: [],
      вид: ВИД,
      разметка: РАЗМЕТКА,
      итоги: [скоростьШара, путьПоПокрытию],
      вопрос: {
        вопрос: 'Шар скатился и покатился по ткани. Что с ним будет дальше?',
        варианты: ['будет катиться, пока не упадёт со стола', 'постепенно остановится', 'сразу остановится'],
        верный: 1,
        пояснение: 'Скорость шара уменьшается понемногу, и он останавливается. Значит, что-то на него действует — подумай что.',
        раскрыть: 'после остановки',
      },
    },
    {
      id: 'pokrytiya',
      название: 'Действие покрытия',
      задание: 'Меняй покрытие стола и измеряй, сколько шар проходит по нему.',
      начальные: { высота: 0.1, покрытие: 'песок' },
      сцена: сценаГорки,
      управление: [ПОКРЫТИЕ, ВЫСОТА],
      показать: { силы: true, значения: false, скорость: true, стробоскоп: false },
      приборы: ['линейка', 'секундомер'],
      вид: ВИД,
      разметка: РАЗМЕТКА,
      итоги: [путьПоПокрытию, скоростьШара],
      вопрос: {
        вопрос: 'По какому покрытию шар с той же высоты уедет дальше всего?',
        варианты: ['по песку', 'по асфальту', 'по льду'],
        верный: 2,
        пояснение: 'Чем слабее покрытие действует на шар, тем дальше он катится. Шар останавливает действие стола на него — песок действует сильно, лёд — слабо.',
        раскрыть: 'после остановки',
      },
    },
    {
      id: 'sverhu',
      название: 'Вывод Галилея',
      задание: 'Шесть одинаковых шаров скатываются разом — каждый на своё покрытие.',
      начальные: { высота: 0.1 },
      сцена: сценаГорки,
      управление: [ВЫСОТА],
      показать: { силы: false, значения: false, скорость: false, стробоскоп: false },
      приборы: [],
      вид: ВИД,
      дорожки: { ключ: 'покрытие', значения: ПОКРЫТИЯ, тело: 'шар', ноль: НАЧАЛО_ПОКРЫТИЯ, длина: ДЛИНА_СТОЛА - НАЧАЛО_ПОКРЫТИЯ, разгон: ВЕРХ / Math.tan(УГОЛ) + НАЧАЛО_ПОКРЫТИЯ },
      вопрос: {
        вопрос: 'А если бы стол совсем не действовал на шар — что было бы с его скоростью?',
        варианты: ['шар сразу остановился бы', 'шар катился бы, не меняя скорости', 'шар разгонялся бы'],
        верный: 1,
        пояснение: 'На идеально гладком столе шар не останавливается. Вывод Галилея: если на тело не действуют другие тела, оно сохраняет свою скорость.',
        раскрыть: 'после пуска',
      },
    },
    {
      id: 'laboratoriya',
      название: 'Свой опыт',
      задание: 'Подбери горку, шар и покрытие сам и проверь свои догадки.',
      начальные: { высота: 0.15, покрытие: 'асфальт', шар: 'сталь', радиус: 0.02 },
      сцена: сценаГорки,
      управление: [
        ВЫСОТА,
        ПОКРЫТИЕ,
        { вид: 'выбор', ключ: 'шар', подпись: 'Шар', варианты: ШАРЫ },
        { вид: 'ползунок', ключ: 'радиус', подпись: 'Радиус шара', min: 0.01, max: 0.04, шаг: 0.005, единица: 'см', множитель: 100, знаков: 1 },
      ],
      показать: { силы: true, значения: true, скорость: true, стробоскоп: false },
      приборы: ['линейка', 'секундомер'],
      вид: ВИД,
      разметка: РАЗМЕТКА,
      итоги: [путьПоПокрытию, скоростьШара],
    },
  ],
}
